import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useI18n } from '../../i18n'
import { usePlan } from '../../hooks/use-plan'
import { useAuth } from '../../hooks/use-auth'
import { Loader2, Receipt, CheckCircle2, XCircle, Clock, RotateCcw, Ban, X, CreditCard, ChevronLeft, ChevronRight, Timer, FileText } from 'lucide-react'
import { PaymentQRCode } from './PaymentQRCode'
import { PaymentResult } from './PaymentResult'
import type { OrderItem, PayStatus } from '../../types'

const statusConfig: Record<string, { zh: string; en: string; dot: string; icon: typeof CheckCircle2; text: string }> = {
  PAID:     { zh: '已支付',  en: 'Paid',       dot: 'bg-emerald-400', icon: CheckCircle2, text: 'text-emerald-600' },
  PENDING:  { zh: '待支付',  en: 'Pending',    dot: 'bg-amber-400',   icon: Clock,        text: 'text-amber-600' },
  EXPIRED:  { zh: '已过期',  en: 'Expired',    dot: 'bg-gray-400',    icon: Timer,         text: 'text-gray-400' },
  FAILED:   { zh: '支付失败', en: 'Failed',    dot: 'bg-red-400',     icon: XCircle,       text: 'text-red-500' },
  REFUNDED: { zh: '已退款',  en: 'Refunded',   dot: 'bg-violet-400',  icon: RotateCcw,     text: 'text-violet-600' },
  CLOSED:   { zh: '已关闭',  en: 'Closed',     dot: 'bg-gray-300',    icon: Ban,           text: 'text-gray-400' },
}

const orderTypeLabels: Record<string, { zh: string; en: string }> = {
  NEW:     { zh: '新购', en: 'New' },
  UPGRADE: { zh: '升级', en: 'Upgrade' },
  RENEW:   { zh: '续费', en: 'Renew' },
  TOPUP:   { zh: '加购', en: 'Top-up' },
}

const channelLabels: Record<string, { zh: string; en: string }> = {
  ALIPAY:  { zh: '支付宝', en: 'Alipay' },
  WECHAT:  { zh: '微信支付', en: 'WeChat Pay' },
}

function formatTime(iso: string | null | undefined) {
  if (!iso) return '-'
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function formatAmount(n: number) {
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatTokenQuota(n: number, lang: string) {
  if (n >= 10000000) return lang === 'zh' ? `${(n / 10000000).toFixed(0)}千万` : `${(n / 1000000).toFixed(0)}M`
  if (n >= 10000) return lang === 'zh' ? `${(n / 10000).toFixed(0)}万` : `${(n / 1000).toFixed(0)}K`
  return n.toLocaleString()
}

function getDisplayStatus(order: OrderItem): string {
  if (order.payStatus === 'PENDING' && order.expireTime && Date.now() > new Date(order.expireTime).getTime()) {
    return 'EXPIRED'
  }
  return order.payStatus
}

/* ── 订单详情弹窗 ── */
function OrderDetailDialog({ orderNo, open, onOpenChange, onResume }: {
  orderNo: string
  open: boolean
  onOpenChange: (v: boolean) => void
  onResume: (order: OrderItem) => void
}) {
  const { lang, t } = useI18n()
  const { fetchOrderDetail } = usePlan()
  const [visible, setVisible] = useState(false)
  const [order, setOrder] = useState<OrderItem | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (open) {
      setLoading(true)
      document.body.style.overflow = 'hidden'
      requestAnimationFrame(() => setVisible(true))
      fetchOrderDetail(orderNo).then((data) => {
        setOrder(data)
        setLoading(false)
      })
    } else {
      setVisible(false)
      const timer = setTimeout(() => { document.body.style.overflow = '' }, 300)
      return () => clearTimeout(timer)
    }
    return () => { document.body.style.overflow = '' }
  }, [open, orderNo])

  if (!open) return null

  const displayStatus = order ? getDisplayStatus(order) : 'PENDING'
  const st = statusConfig[displayStatus] || statusConfig.PENDING
  const ot = orderTypeLabels[order?.orderType || 'NEW'] || orderTypeLabels.NEW
  const ch = channelLabels[order?.payChannel || 'ALIPAY'] || channelLabels.ALIPAY
  const Icon = st.icon
  const isPending = displayStatus === 'PENDING'

  const dialog = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl border border-black/[0.06] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
          transition: 'opacity 300ms cubic-bezier(0.16,1,0.3,1), transform 300ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 rounded-b-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 opacity-60" />

        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 className="text-sm font-semibold text-[#1a1a1a]">{t('订单详情', 'Order Detail')}</h2>
          <button onClick={() => onOpenChange(false)} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pb-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin mb-3" />
              <p className="text-sm text-gray-400">{t('加载中...', 'Loading...')}</p>
            </div>
          ) : order ? (
            <>
              {/* 状态 + 金额 */}
              <div className="text-center mb-5 p-4 rounded-xl bg-gray-50/80">
                <div className={`inline-flex items-center gap-1.5 text-xs font-medium mb-2 ${st.text}`}>
                  <Icon className="h-4 w-4" />
                  {t(st.zh, st.en)}
                </div>
                <div className="text-3xl font-bold text-[#1a1a1a]">¥{formatAmount(order.amount)}</div>
                {order.originalAmount != null && order.originalAmount !== order.amount && (
                  <div className="text-xs text-gray-400 mt-1 line-through">¥{formatAmount(order.originalAmount)}</div>
                )}
              </div>

              {/* 详情行 */}
              <div className="space-y-2.5 text-sm">
                <DetailRow label={t('订单号', 'Order No')} value={order.orderNo} mono />
                <DetailRow label={t('套餐', 'Plan')} value={order.planCode} />
                <DetailRow label={t('类型', 'Type')} value={t(ot.zh, ot.en)} />
                {order.billingCycle && (
                  <DetailRow label={t('计费周期', 'Billing')} value={order.billingCycle === 'MONTHLY' ? t('月付', 'Monthly') : t('年付', 'Yearly')} />
                )}
                <DetailRow label={t('支付方式', 'Channel')} value={t(ch.zh, ch.en)} />
                <DetailRow label={t('Token 额度', 'Token Quota')} value={formatTokenQuota(order.tokenQuota, lang)} />
                {order.durationDays > 0 && (
                  <DetailRow label={t('有效天数', 'Duration')} value={`${order.durationDays} ${t('天', 'days')}`} />
                )}
                <DetailRow label={t('创建时间', 'Created')} value={formatTime(order.createTime)} />
                {order.payTime && <DetailRow label={t('支付时间', 'Paid at')} value={formatTime(order.payTime)} />}
                {order.expireTime && (
                  <DetailRow label={t('过期时间', 'Expires')} value={formatTime(order.expireTime)} />
                )}
                {order.remark && <DetailRow label={t('备注', 'Remark')} value={order.remark} />}
              </div>

              {/* 操作 */}
              {isPending && (
                <button
                  onClick={() => { onOpenChange(false); onResume(order) }}
                  className="w-full h-10 mt-5 bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-medium rounded-xl transition-all flex items-center justify-center gap-2"
                >
                  <CreditCard className="h-4 w-4" />
                  {t('继续支付', 'Pay Now')}
                </button>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-sm text-gray-400">{t('获取订单详情失败', 'Failed to load order detail')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(dialog, document.body)
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-black/[0.03] last:border-0">
      <span className="text-gray-400 text-xs">{label}</span>
      <span className={`text-[#1a1a1a] text-xs font-medium text-right max-w-[60%] truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  )
}

/* ── 继续支付弹窗 ── */
function ResumePayDialog({ order, open, onOpenChange, onSuccess }: {
  order: OrderItem
  open: boolean
  onOpenChange: (v: boolean) => void
  onSuccess: () => void
}) {
  const { t } = useI18n()
  const { createOrder } = usePlan()
  const { refreshUserInfo } = useAuth()
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState<'loading' | 'qr' | 'result'>('loading')
  const [orderNo, setOrderNo] = useState('')
  const [qrCodeUrl, setQrCodeUrl] = useState('')
  const [expireSeconds, setExpireSeconds] = useState(1800)
  const [resultStatus, setResultStatus] = useState<PayStatus>('PENDING')
  const [error, setError] = useState('')

  useEffect(() => {
    if (open) {
      setStep('loading')
      setError('')
      document.body.style.overflow = 'hidden'
      requestAnimationFrame(() => setVisible(true))

      const request: any = {
        planCode: order.planCode,
        payChannel: order.payChannel,
      }
      if (order.billingCycle) {
        request.billingCycle = order.billingCycle
      }
      createOrder(request).then((res) => {
        if (res) {
          setOrderNo(res.orderNo)
          setQrCodeUrl(res.qrCodeUrl)
          if (res.expireTime) {
            const expire = new Date(res.expireTime).getTime()
            const secs = Math.max(0, Math.floor((expire - Date.now()) / 1000))
            setExpireSeconds(secs)
          }
          setStep('qr')
        } else {
          setError(t('获取支付二维码失败', 'Failed to get payment QR code'))
          setStep('qr')
        }
      })
    } else {
      setVisible(false)
      const timer = setTimeout(() => { document.body.style.overflow = '' }, 300)
      return () => clearTimeout(timer)
    }
    return () => { document.body.style.overflow = '' }
  }, [open])

  if (!open) return null

  const handleStatusChange = (status: PayStatus) => {
    setResultStatus(status)
    setStep('result')
    if (status === 'PAID') {
      onSuccess()
      refreshUserInfo()
    }
  }

  const dialog = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
        style={{ opacity: visible ? 1 : 0 }}
        onClick={() => onOpenChange(false)}
      />
      <div
        className="relative w-full max-w-md bg-white rounded-2xl border border-black/[0.06] shadow-[0_24px_48px_-12px_rgba(0,0,0,0.18)] overflow-hidden"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(8px)',
          transition: 'opacity 300ms cubic-bezier(0.16,1,0.3,1), transform 300ms cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-1 rounded-b-full bg-gradient-to-r from-orange-400 via-amber-400 to-orange-400 opacity-60" />
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <h2 className="text-sm font-semibold text-[#1a1a1a]">{t('继续支付', 'Resume Payment')}</h2>
          <button onClick={() => onOpenChange(false)} className="p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-all">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 pb-5">
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-100 text-red-500 text-xs">{error}</div>
          )}
          {step === 'loading' && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-6 w-6 text-orange-500 animate-spin mb-3" />
              <p className="text-sm text-gray-400">{t('获取支付信息...', 'Getting payment info...')}</p>
            </div>
          )}
          {step === 'qr' && orderNo && (
            <PaymentQRCode
              orderId={orderNo}
              qrCodeUrl={qrCodeUrl}
              expireSeconds={expireSeconds}
              channel={order.payChannel}
              onStatusChange={handleStatusChange}
            />
          )}
          {step === 'result' && (
            <PaymentResult
              status={resultStatus}
              onRetry={() => setStep('qr')}
              onClose={() => onOpenChange(false)}
            />
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(dialog, document.body)
}

/* ── 单条订单卡片 ── */
function OrderCard({ order, onResume, onDetail }: { order: OrderItem; onResume: (order: OrderItem) => void; onDetail: (orderNo: string) => void }) {
  const { t } = useI18n()
  const displayStatus = getDisplayStatus(order)
  const st = statusConfig[displayStatus] || statusConfig.PENDING
  const ot = orderTypeLabels[order.orderType] || orderTypeLabels.NEW
  const ch = channelLabels[order.payChannel] || channelLabels.ALIPAY
  const Icon = st.icon
  const isPending = displayStatus === 'PENDING'

  return (
    <div
      onClick={() => onDetail(order.orderNo)}
      className={`group relative p-4 rounded-2xl border transition-all duration-200 cursor-pointer ${
        isPending
          ? 'bg-gradient-to-br from-orange-50/50 to-amber-50/30 border-orange-100/60 hover:border-orange-200 hover:shadow-[0_4px_20px_rgba(249,115,22,0.08)]'
          : 'bg-white border-black/[0.04] hover:border-black/[0.08] hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[#1a1a1a]">{order.planCode}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-500 font-medium">
            {t(ot.zh, ot.en)}
          </span>
        </div>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium ${st.text}`}>
          <Icon className="h-3.5 w-3.5" />
          {t(st.zh, st.en)}
        </span>
      </div>

      <div className="mb-3">
        <span className="text-2xl font-bold text-[#1a1a1a]">¥{formatAmount(order.amount)}</span>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          <span>{formatTime(order.createTime)}</span>
          <span>{t(ch.zh, ch.en)}</span>
          {order.billingCycle && (
            <span>{order.billingCycle === 'MONTHLY' ? t('月付', 'Monthly') : t('年付', 'Yearly')}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-300 group-hover:text-orange-400 transition-colors flex items-center gap-0.5">
            <FileText className="h-3 w-3" />
            {t('详情', 'Detail')}
          </span>
          {isPending && (
            <button
              onClick={(e) => { e.stopPropagation(); onResume(order) }}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-medium text-white bg-orange-500 hover:bg-orange-600 active:scale-[0.97] transition-all"
            >
              <CreditCard className="h-3 w-3" />
              {t('继续支付', 'Pay Now')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── 分页 ── */
function Pagination({ page, totalPages, total, pageSize, onPageChange, onPageSizeChange }: {
  page: number
  totalPages: number
  total: number
  pageSize: number
  onPageChange: (p: number) => void
  onPageSizeChange: (size: number) => void
}) {
  const { t } = useI18n()
  if (total === 0) return null

  const pageSizes = [10, 20, 50]

  return (
    <div className="flex items-center justify-between mt-6 pt-4 border-t border-black/[0.04]">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-400">{t('每页', 'Per page')}</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-7 px-2 rounded-lg border border-black/[0.06] bg-white text-xs text-[#1a1a1a] focus:border-orange-200 focus:outline-none cursor-pointer"
        >
          {pageSizes.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="text-xs text-gray-400">{t('共 {n} 条', `${total} total`).replace('{n}', String(total))}</span>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 0}
            className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                p === page
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04]'
              }`}
            >
              {p + 1}
            </button>
          ))}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages - 1}
            className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

/* ── 订单列表 ── */
export function OrderList() {
  const { t } = useI18n()
  const { orders, orderPage, orderTotalPages, orderTotal, fetchOrders, loading } = usePlan()
  const [resumeOrder, setResumeOrder] = useState<OrderItem | null>(null)
  const [detailOrderNo, setDetailOrderNo] = useState<string | null>(null)
  const [pageSize, setPageSize] = useState(10)

  useEffect(() => {
    fetchOrders(1, pageSize)
  }, [])

  const handleResumeSuccess = () => {
    setResumeOrder(null)
    fetchOrders(orderPage + 1, pageSize)
  }

  const handlePageChange = (newPage: number) => {
    fetchOrders(newPage + 1, pageSize)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    fetchOrders(1, newSize)
  }

  if (loading && orders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="h-6 w-6 text-orange-500 animate-spin mb-3" />
        <p className="text-sm text-gray-400">{t('加载订单中...', 'Loading orders...')}</p>
      </div>
    )
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 rounded-2xl bg-orange-50 flex items-center justify-center mx-auto mb-4">
          <Receipt className="h-8 w-8 text-orange-300" />
        </div>
        <p className="text-sm text-gray-400 mb-1">{t('暂无订单记录', 'No orders yet')}</p>
        <p className="text-xs text-gray-300">{t('购买套餐后订单会显示在这里', 'Orders will appear here after purchase')}</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-3">
        {orders.map((order) => (
          <OrderCard key={order.id} order={order} onResume={setResumeOrder} onDetail={setDetailOrderNo} />
        ))}
      </div>

      <Pagination page={orderPage} totalPages={orderTotalPages} total={orderTotal} pageSize={pageSize} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />

      {detailOrderNo && (
        <OrderDetailDialog
          orderNo={detailOrderNo}
          open={!!detailOrderNo}
          onOpenChange={(v) => { if (!v) setDetailOrderNo(null) }}
          onResume={(order) => { setDetailOrderNo(null); setResumeOrder(order) }}
        />
      )}

      {resumeOrder && (
        <ResumePayDialog
          order={resumeOrder}
          open={!!resumeOrder}
          onOpenChange={(v) => { if (!v) setResumeOrder(null) }}
          onSuccess={handleResumeSuccess}
        />
      )}
    </>
  )
}
